import React, { useState, useEffect } from 'react';
import { updateUserData, getRoleName, getUsersByOrganization, getSubTeamleiter, setSubTeamleiter, getTeamsByOrganization, addUserToTeam, removeUserFromTeam, getAgenturenByOrganization, getTeamsByAgentur, getAgenturAllowedTeams } from '../../api';

function EditUserDialog({ user, targetUser, onSave, onCancel, isMobile, isTablet }) {
  const [formData, setFormData] = useState({
    fullName: targetUser?.full_name || '',
    email: targetUser?.email || '',
    roleType: targetUser?.role_type || '0',
    newPassword: ''
  });
  
  // Berechtigungen für Rollenänderung prüfen
  const isAdmin = user?.isAdmin || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isTeamLeader = user?.isTeamlead || false;
  const isAgenturLeader = user?.isAgenturLeader || false;
  
  // Verfügbare Rollen basierend auf Berechtigungen
  const getAvailableRoles = () => {
    // Bestehende Callcenter-Rolle als beizubehaltende Option anzeigen (sie kann hier nicht neu vergeben,
    // aber bei einem CC-User beibehalten werden, damit z. B. das Passwort gesetzt werden kann).
    const targetRt = String(targetUser?.role_type);
    const ccOption = targetRt === '4' ? [{ value: '4', label: 'Callcenter Leader' }]
                   : targetRt === '5' ? [{ value: '5', label: 'Callcenter Mitarbeiter' }]
                   : [];

    if (isAdmin) {
      return [
        ...ccOption,
        { value: '2', label: 'Teamleiter' },
        { value: '3', label: 'Organisationsleiter' },
        { value: '6', label: 'Fachberater' },
        { value: '7', label: 'Agentur Leiter' },
        { value: '0', label: 'Benutzer' }
      ];
    } else if (isOrgLeader) {
      return [
        ...ccOption,
        { value: '2', label: 'Teamleiter' },
        { value: '6', label: 'Fachberater' },
        { value: '7', label: 'Agentur Leiter' },
        { value: '0', label: 'Benutzer' }
      ];
    } else if (isAgenturLeader) {
      // Teamleiter können keine Rollen ändern
      return [
        { value: '2', label: 'Teamleiter' },
        { value: '0', label: 'Benutzer' }
      ];
    }
    return [];
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [teamleiterId, setTeamleiterId] = useState('');
  const [allMembers, setAllMembers] = useState([]);
  const [teamleiterLoading, setTeamleiterLoading] = useState(true);
  const [teamId, setTeamId] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [agenturen, setAgenturen] = useState([]);
  const [agenturId, setAgenturId] = useState(targetUser?.agentur_id ? String(targetUser.agentur_id) : '');
  const [agenturenLoading, setAgenturenLoading] = useState(true);
  const [agenturTeams, setAgenturTeams] = useState([]);
  const [selectedAgenturTeamId, setSelectedAgenturTeamId] = useState(targetUser?.a_team_id ? String(targetUser.a_team_id) : '');
  const [allowedOrgTeams, setAllowedOrgTeams] = useState([]);
  const [loadingAllowedOrgTeams, setLoadingAllowedOrgTeams] = useState(false);

  // Mitglieder, Teams und aktuellen Teamleiter/Team laden
  useEffect(() => {
    const loadData = async () => {
      setTeamleiterLoading(true);
      setTeamsLoading(true);
      setAgenturenLoading(true);
      // Alle Mitglieder der Organisation laden
      let orgId = targetUser.organization_id || user.organization_id;
      let membersResp = await getUsersByOrganization(user.id, orgId);
      let teamsResp = await getTeamsByOrganization(user.id, orgId);
      let teamleiterResp = await getSubTeamleiter(targetUser.id);
      let agenturenResp = await getAgenturenByOrganization(orgId);
      if (membersResp.success) setAllMembers(membersResp.users);
      if (teamsResp.success) setTeams(teamsResp.teams);
      if (agenturenResp.success && Array.isArray(agenturenResp.agenturen)) setAgenturen(agenturenResp.agenturen);
      // TeamId direkt vom User nehmen!
      if (targetUser.team_id) {
        setTeamId(targetUser.team_id.toString());
      } else {
        setTeamId('');
      }
      // AgenturId direkt vom User nehmen!
      console.log("TargetUser : ", targetUser)
      if (targetUser.agentur_id) {
        setAgenturId(String(targetUser.agentur_id));
      } else {
        setAgenturId('');
      }
      // Teamleiter-Logik bleibt wie gehabt
      if (teamleiterResp.success) {
        if (teamleiterResp.teamleiter_id) {
          setTeamleiterId(teamleiterResp.teamleiter_id.toString());
        } else {
          setTeamleiterId('');
        }
      } else {
        setTeamleiterId('');
      }
      setTeamleiterLoading(false);
      setTeamsLoading(false);
      setAgenturenLoading(false);
    };
    loadData();
  }, [targetUser, user]);

  // Teams der Agentur laden, wenn Agentur gewählt (bei Agenturleiter auch user.agentur_id)
  useEffect(() => {
    const effectiveAgenturId = agenturId || (isAgenturLeader && user?.agentur_id ? String(user.agentur_id) : '');
    if (effectiveAgenturId) {
      getTeamsByAgentur(user.id, effectiveAgenturId).then((res) => {
        if (res.success) setAgenturTeams(res.teams || []);
        else setAgenturTeams([]);
      });
    } else {
      setAgenturTeams([]);
      setSelectedAgenturTeamId('');
    }
  }, [agenturId, user.id, user?.agentur_id, isAgenturLeader]);

  useEffect(() => {
    if (!isAgenturLeader || !user?.agentur_id || !user?.id) {
      setAllowedOrgTeams([]);
      setLoadingAllowedOrgTeams(false);
      return;
    }
    let cancelled = false;
    setLoadingAllowedOrgTeams(true);
    getAgenturAllowedTeams(user.id, user.agentur_id).then((res) => {
      if (cancelled) return;
      if (res.success) setAllowedOrgTeams(res.teams || []);
      else setAllowedOrgTeams([]);
      setLoadingAllowedOrgTeams(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAgenturLeader, user?.agentur_id, user?.id]);

  // Nur freigegebene Projekte: wenn gespeichertes team_id nicht in der Liste ist, Auswahl leeren
  useEffect(() => {
    if (!isAgenturLeader || loadingAllowedOrgTeams) return;
    const ids = new Set(allowedOrgTeams.map((t) => Number(t.id)));
    if (targetUser?.team_id && ids.size > 0) {
      if (ids.has(Number(targetUser.team_id))) {
        setTeamId(String(targetUser.team_id));
      } else {
        setTeamId('');
      }
    }
  }, [isAgenturLeader, loadingAllowedOrgTeams, allowedOrgTeams, targetUser?.id, targetUser?.team_id]);

  const agenturLeaderTeamNotInAllowedList =
    isAgenturLeader &&
    !loadingAllowedOrgTeams &&
    Boolean(targetUser?.team_id) &&
    allowedOrgTeams.length > 0 &&
    !allowedOrgTeams.some((t) => Number(t.id) === Number(targetUser.team_id));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'agenturId') {
      setAgenturId(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Debug-Ausgabe
      console.log('selectedAgenturTeamId:', selectedAgenturTeamId);
      console.log('Wird gesendet:', {
        ...formData,
        agentur_id: agenturId,
        a_team_id: selectedAgenturTeamId
      });

      if (teamId !== (targetUser.team_id ? String(targetUser.team_id) : '')) {

        if (targetUser.team_id) {
          await removeUserFromTeam(user.id, targetUser.id, targetUser.team_id);
        }

        if (teamId) {
          await addUserToTeam(user.id, targetUser.id, teamId);
        }
      }

      // 2. Benutzerdaten speichern (inkl. agentur_id)
      console.log("AgenturID : ", agenturId)
      const response = await updateUserData(user.id, targetUser.id, { ...formData, agentur_id: agenturId, a_team_id:  selectedAgenturTeamId});
      console.log("Antwort : ", response)
      let orgId = targetUser.organization_id || user.organization_id;
      let teamleiterRes = { success: true };
      if (teamleiterId) {
        teamleiterRes = await setSubTeamleiter(targetUser.id, teamleiterId, orgId, teamId);
      }
      if (response.success && teamleiterRes.success) {
        onSave();
      } else {
        setError((response.error || '') + ' ' + (teamleiterRes.error || ''));
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    },
    modal: {
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: isMobile ? '95%' : '500px',
      maxHeight: '90vh',
      overflow: 'auto'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      borderBottom: '1px solid #e2e8f0'
    },
    title: {
      margin: 0,
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      color: '#2d3748'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#718096',
      padding: '0',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    content: {
      padding: '20px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px'
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: '500',
      color: '#4a5568'
    },
    input: {
      padding: '10px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.9rem',
      width: '100%',
      boxSizing: 'border-box'
    },
    select: {
      padding: '10px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.9rem',
      width: '100%',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    errorMessage: {
      color: '#e53e3e',
      fontSize: '0.8rem',
      marginTop: '5px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      marginTop: '20px',
      flexDirection: isMobile ? 'column' : 'row'
    },
    button: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '0.9rem',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'background-color 0.2s',
      width: isMobile ? '100%' : 'auto'
    },
    saveButton: {
      background: '#38a169',
      color: 'white'
    },
    cancelButton: {
      background: '#e2e8f0',
      color: '#4a5568'
    },
    loadingButton: {
      background: '#a0aec0',
      color: 'white',
      cursor: 'not-allowed'
    }
  };

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Benutzer bearbeiten</h3>
          <button style={styles.closeButton} onClick={onCancel}>
            ×
          </button>
        </div>
        
        <div style={styles.content}>
          <form style={styles.form} onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name:</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                style={styles.input}
                required
                placeholder="Vollständiger Name"
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>E-Mail:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={styles.input}
                required
                placeholder="E-Mail-Adresse"
              />
            </div>

            {/* Neues Passwort (optional) - nur Admin/Organisationsleiter */}
            {(user?.isAdmin || user?.isOrgLeader) && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Neues Passwort:</label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  style={styles.input}
                  autoComplete="new-password"
                  placeholder="Leer lassen = Passwort unverändert"
                  minLength={6}
                />
                <small style={{ color: '#718096', fontSize: '12px' }}>
                  Nur ausfüllen, um das Passwort des Mitarbeiters zu ändern (mind. 6 Zeichen).
                </small>
              </div>
            )}

              {/* Rollen Dropdown */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Rolle:</label>
              {getAvailableRoles().length > 0 ? (
                <select
                  name="roleType"
                  value={formData.roleType}
                  onChange={handleInputChange}
                  style={styles.select}
                  required
                >
                  {getAvailableRoles().map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ 
                  padding: '10px', 
                  backgroundColor: '#f7fafc', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '4px',
                  color: '#718096',
                  fontSize: '0.9rem'
                }}>
                  Keine Berechtigung zur Rollenänderung
                </div>
              )}
            </div>

            {isAgenturLeader && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Projekt:</label>
                {loadingAllowedOrgTeams ? (
                  <div style={{ fontSize: '0.9rem', color: '#718096' }}>Projekte werden geladen...</div>
                ) : allowedOrgTeams.length === 0 ? (
                  <div
                    style={{
                      padding: '10px',
                      backgroundColor: '#fffaf0',
                      border: '1px solid #ed8936',
                      borderRadius: '4px',
                      color: '#744210',
                      fontSize: '0.9rem',
                    }}
                  >
                    Keine Projekte für diese Agentur freigegeben. Ein Organisationsleiter kann sie unter „Agenturen
                    verwalten“ bei der Agentur zuweisen.
                  </div>
                ) : (
                  <>
                    {agenturLeaderTeamNotInAllowedList && (
                      <div
                        style={{
                          marginBottom: '8px',
                          padding: '8px 10px',
                          backgroundColor: '#fff5f5',
                          border: '1px solid #fc8181',
                          borderRadius: '4px',
                          color: '#742a2a',
                          fontSize: '0.85rem',
                        }}
                      >
                        Das bisher zugewiesene Projekt ist nicht mehr freigegeben. Bitte ein Projekt aus der Liste
                        wählen.
                      </div>
                    )}
                    <select
                      name="teamId"
                      value={
                        allowedOrgTeams.some((t) => Number(t.id) === Number(teamId))
                          ? teamId
                          : ''
                      }
                      onChange={(e) => setTeamId(e.target.value)}
                      style={styles.select}
                    >
                      <option value="">Kein Projekt</option>
                      {allowedOrgTeams.map((team) => (
                        <option key={team.id} value={String(team.id)}>
                          {team.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}

            {/* Projektleiter Dropdown */}
            {!isAgenturLeader && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Teamleiter:</label>
                {teamleiterLoading ? (
                  <div>Teamleiter werden geladen...</div>
                ) : (
                  <select
                    name="teamleiterId"
                    value={teamleiterId}
                    onChange={e => setTeamleiterId(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Kein Teamleiter</option>
                    {allMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.full_name} ({member.email})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}  

              {/* Projekt Dropdown */}
            {!isAgenturLeader && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Projekt:</label>
                {teamsLoading ? (
                  <div>Projekte werden geladen...</div>
                ) : (
                  <select
                    name="teamId"
                    value={teamId}
                    onChange={e => setTeamId(e.target.value)}
                    style={styles.select}
                  >
                    <option value="">Kein Projekt</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Agentur Dropdown */}
            {!isAgenturLeader && (
              <>
              <div style={styles.formGroup}>
                <label style={styles.label}>Agentur:</label>
                {agenturenLoading ? (
                  <div>Agenturen werden geladen...</div>
                ) : (
                  <select
                    name="agenturId"
                    value={agenturId}
                    onChange={handleInputChange}
                    style={styles.select}
                  >
                    <option value="">Keine Agentur</option>
                    {agenturen.map(agentur => (
                      <option key={agentur.id} value={agentur.id}>
                        {agentur.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              </>
            )}

            {/* Agentur-internes Team */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Agentur-Team:</label>
              <select
                name="agenturTeamId"
                value={selectedAgenturTeamId}
                onChange={e => setSelectedAgenturTeamId(e.target.value)}
                style={styles.select}
                disabled={!agenturId && !(isAgenturLeader && user?.agentur_id)}
              >
                <option value="">Kein Team</option>
                {agenturTeams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
            
            {error && (
              <div style={styles.errorMessage}>
                {error}
              </div>
            )}
            
            <div style={styles.buttonGroup}>
              <button
                type="button"
                onClick={onCancel}
                style={styles.cancelButton}
                disabled={loading}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...styles.saveButton,
                  ...(loading && styles.loadingButton)
                }}
                disabled={loading}
              >
                {loading ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditUserDialog; 